import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "../../services/apiError";
import { ARCHIVE_AFTER_DAYS, taskCount } from "./archivePolicy";

// An armed "Archive N tasks?" disarms itself (house rule).
const DISARM_MS = 5000;

/**
 * Under the Done column: says what happens to finished tasks, how many are
 * already in the archive (a link to it), and offers "Archive all done" —
 * a two-step confirm that disarms itself.
 */
function DoneColumnFooter({
  doneCount,
  archivedCount = null,
  archiveHref = "/tasks/archive",
  onArchiveAll,
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const trigger = useRef(null);
  const keep = useRef(null);
  // Focus follows the confirm (the buttons it replaces unmount), so a
  // keyboard user never lands on <body>.
  const focusSoon = (ref) => requestAnimationFrame(() => ref.current?.focus());

  useEffect(() => () => clearTimeout(timer.current), []);

  const arm = () => {
    setNote(null);
    setError(null);
    setArmed(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setArmed(false);
      focusSoon(trigger);
    }, DISARM_MS);
    focusSoon(keep);
  };

  const run = async () => {
    clearTimeout(timer.current);
    setBusy(true);
    setError(null);
    try {
      const archived = await onArchiveAll();
      setNote(`Archived ${taskCount(archived)} — they're in the archive.`);
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't archive the Done tasks."));
    } finally {
      setBusy(false);
      setArmed(false);
      focusSoon(trigger);
    }
  };

  return (
    <div className="mt-3 border-t pt-2 text-xs text-muted-foreground">
      <p className="mb-1">
        Done tasks move to the archive {ARCHIVE_AFTER_DAYS} days after they
        finish.{" "}
        <Link to={archiveHref} className="underline underline-offset-2">
          {archivedCount
            ? `${taskCount(archivedCount)} archived — view archive`
            : "View archive"}
        </Link>
      </p>
      {doneCount > 0 &&
        (armed ? (
          <div className="flex flex-wrap items-center gap-1">
            <span>Archive {taskCount(doneCount)} now?</span>{" "}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={run}
            >
              {busy ? "Archiving…" : "Archive"}
            </Button>{" "}
            <Button
              ref={keep}
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                clearTimeout(timer.current);
                setArmed(false);
                focusSoon(trigger);
              }}
            >
              Keep
            </Button>
          </div>
        ) : (
          <Button
            ref={trigger}
            type="button"
            size="sm"
            variant="ghost"
            className="-ml-2 h-7 px-2"
            onClick={arm}
          >
            Archive all done
          </Button>
        ))}
      {note && (
        <p role="status" className="mt-1">
          {note}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-1 text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

DoneColumnFooter.propTypes = {
  doneCount: PropTypes.number.isRequired,
  archivedCount: PropTypes.number,
  archiveHref: PropTypes.string,
  onArchiveAll: PropTypes.func.isRequired,
};

export default DoneColumnFooter;
